import { useEffect, useReducer, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { Alert } from '../../components/Alert';
import { Button } from '../../components/Button';
import { CookIngredients } from '../../components/plans/CookIngredients';
import { MarkCookedDialog } from '../../components/plans/MarkCookedDialog';
import { StepTimer } from '../../components/plans/StepTimer';
import { TimelineLegend } from '../../components/plans/TimelineView';
import { TimelineStepCard } from '../../components/plans/TimelineStepCard';
import { fireAlarm, primeAlarm } from '../../components/plans/alarm';
import { useNow } from '../../components/plans/useNow';
import { useWakeLock } from '../../components/plans/useWakeLock';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { maybeRequestNotificationPermission, notifyTimerDone } from '../../lib/cookNotifications';
import { messageForError } from '../../lib/errors';
import { formatClock, formatDuration } from '../../lib/planDates';
import { cookedToast, reanchorLate, splitTimeline } from '../../lib/plans';
import {
  emptyTimers,
  loadTimers,
  saveTimers,
  timersReducer,
  unacknowledgedTimers,
} from '../../lib/timers';

const MUTE_KEY = 'fewerbags:cook:muted';

function initialMuted(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Cooking mode: one screen, big type, the screen kept awake, per-step timers
 * and a "now" marker. Everything that matters survives a reload — the timers
 * live in sessionStorage and the timeline is re-anchored on demand.
 */
export function CookPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const system = auth.household?.unitSystem === 'metric' ? 'metric' : 'us';
  const now = useNow(1_000);
  const nowIso = new Date(now).toISOString();

  const [anchor, setAnchor] = useState<string | null>(null);
  const [muted, setMuted] = useState(initialMuted);
  const [keepAwake, setKeepAwake] = useState(true);
  const [cookedOpen, setCookedOpen] = useState(false);
  const [timers, dispatch] = useReducer(timersReducer, id, (planId) =>
    planId ? loadTimers(planId) : emptyTimers,
  );

  const wakeLock = useWakeLock(keepAwake);

  const timeline = useQuery({
    queryKey: queryKeys.plans.timeline(id, undefined, anchor ?? undefined),
    queryFn: () => api.plans.timeline(id, anchor ? { now: anchor } : {}),
    enabled: id !== '',
    // Always the saved serve time: arriving from the plan editor right after
    // changing it must not show a cached schedule.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Drive the state machine off the clock, then fire the alarm for anything
  // that just finished (once — the reducer records that it was played).
  useEffect(() => {
    dispatch({ type: 'tick', now });
  }, [now]);

  // Hidden page (another tab, phone locked): add an OS notification on top of
  // the in-page alarm, titled with the recipe and the step.
  const timelineData = timeline.data;
  useEffect(() => {
    const pending = unacknowledgedTimers(timers);
    if (pending.length === 0) return;
    fireAlarm({ muted });
    for (const timer of pending) {
      const entry = timelineData?.entries.find((e) => e.stepId === timer.stepId);
      notifyTimerDone({
        planId: id,
        stepId: timer.stepId,
        stepText: entry?.text ?? '',
        recipeTitle: entry
          ? (timelineData?.legend.find((l) => l.recipeId === entry.recipeId)?.title ?? '')
          : '',
      });
      dispatch({ type: 'acknowledge', stepId: timer.stepId });
    }
  }, [timers, muted, id, timelineData]);

  useEffect(() => {
    if (id) saveTimers(id, timers);
  }, [id, timers]);

  useEffect(() => {
    try {
      localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch {
      // Private mode — the toggle simply does not persist.
    }
  }, [muted]);

  if (timeline.isPending) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-cream text-base text-muted">
        Working out the order…
      </main>
    );
  }
  if (timeline.isError || !timeline.data) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-3 bg-cream p-4">
        <Alert tone="error">{messageForError(timeline.error)}</Alert>
        <Link to={`/plan/${id}`} className="text-sm text-brand underline">
          Back to the meal
        </Link>
      </main>
    );
  }

  // "Running late": the API reports `too_late` but does not move anything, so
  // the schedule is shifted here to start at the moment the button was pressed.
  const { timeline: data, shiftedMs } = reanchorLate(timeline.data, anchor);
  const { done, current, upcoming } = splitTimeline(data.entries, nowIso);
  const titleFor = (recipeId: string) =>
    data.legend.find((l) => l.recipeId === recipeId)?.title ?? 'This meal';

  const stepTimer = (entry: (typeof data.entries)[number]) =>
    entry.timer && entry.durationMin > 0 ? (
      <StepTimer
        stepId={entry.stepId}
        label={entry.text}
        durationMs={entry.durationMin * 60_000}
        timer={timers[entry.stepId]}
        now={now}
        onStart={(stepId, durationMs) => {
          primeAlarm();
          maybeRequestNotificationPermission();
          dispatch({ type: 'start', stepId, durationMs, now: Date.now() });
        }}
        onPause={(stepId) => dispatch({ type: 'pause', stepId, now: Date.now() })}
        onResume={(stepId) => dispatch({ type: 'resume', stepId, now: Date.now() })}
        onReset={(stepId) => dispatch({ type: 'reset', stepId })}
      />
    ) : null;

  const toggleClass =
    'min-h-11 rounded-lg border border-line bg-surface px-3 text-sm font-semibold text-brand';

  return (
    <div className="min-h-dvh bg-cream">
      <header className="sticky top-0 z-10 border-b border-line bg-cream/95 px-4 py-2 backdrop-blur">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2">
          <Link
            to={`/plan/${id}`}
            className="text-sm text-brand underline-offset-2 hover:underline"
          >
            ← Back to the meal
          </Link>
          <p className="text-lg font-bold text-brand tabular-nums" aria-live="off">
            {formatClock(nowIso)}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={toggleClass}
              aria-pressed={muted}
              onClick={() => {
                if (muted) primeAlarm();
                setMuted((m) => !m);
              }}
            >
              {muted ? 'Sound off' : 'Sound on'}
            </button>
            <button
              type="button"
              className={toggleClass}
              aria-pressed={keepAwake}
              onClick={() => setKeepAwake((k) => !k)}
              disabled={!wakeLock.supported}
              title={
                wakeLock.supported
                  ? 'Keep the screen on while you cook'
                  : 'This browser cannot keep the screen awake'
              }
            >
              {wakeLock.supported ? (keepAwake ? 'Screen on' : 'Screen normal') : 'Screen normal'}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-4 pb-24">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-2xl">Cooking</h1>
          <p className="text-base text-muted">
            Serve at <strong className="text-brand">{formatClock(data.serveAt)}</strong> · started{' '}
            {formatClock(data.startAt)} · {formatDuration(data.totalActiveMin) || 'no'} hands-on
          </p>
        </div>

        <TimelineLegend legend={data.legend} />

        {shiftedMs > 0 ? (
          <Alert tone="warning">Running late: serving moves to {formatClock(data.serveAt)}</Alert>
        ) : null}

        <CookIngredients
          planId={id}
          recipes={data.plan.recipes}
          system={system}
          defaultOpen={current.length === 0 && done.length === 0}
        />

        {!data.feasible || data.conflicts.length > 0 ? (
          <Alert tone={data.feasible ? 'warning' : 'error'}>
            <ul className="flex flex-col gap-1">
              {data.conflicts.map((conflict, i) => (
                <li key={`${conflict.kind}-${i}`}>
                  {conflict.message}
                  {conflict.suggestion ? ` — ${conflict.suggestion}` : ''}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={toggleClass}
            onClick={() => setAnchor(new Date().toISOString())}
            disabled={timeline.isFetching}
          >
            {timeline.isFetching ? 'Re-planning…' : 'I’m running late — re-plan from now'}
          </button>
          {anchor ? (
            <button type="button" className={toggleClass} onClick={() => setAnchor(null)}>
              Back to the original plan
            </button>
          ) : null}
        </div>

        {current.length > 0 ? (
          <section aria-labelledby="cook-now-heading">
            <h2 id="cook-now-heading" className="text-lg">
              Right now
            </h2>
            <ol className="mt-2 flex flex-col gap-2">
              {current.map((entry) => (
                <TimelineStepCard
                  key={`${entry.stepId}-${entry.start}`}
                  entry={entry}
                  recipeTitle={titleFor(entry.recipeId)}
                  size="large"
                >
                  {stepTimer(entry)}
                </TimelineStepCard>
              ))}
            </ol>
          </section>
        ) : null}

        <section aria-labelledby="cook-next-heading">
          <h2 id="cook-next-heading" className="text-lg">
            {current.length > 0 ? 'Next' : 'Coming up'}
          </h2>
          {upcoming.length === 0 ? (
            <p className="mt-2 text-base text-muted">That is everything — time to serve.</p>
          ) : (
            <ol className="mt-2 flex flex-col gap-2">
              {upcoming.map((entry, i) => (
                <TimelineStepCard
                  key={`${entry.stepId}-${entry.start}`}
                  entry={entry}
                  recipeTitle={titleFor(entry.recipeId)}
                  size={i === 0 && current.length === 0 ? 'large' : 'normal'}
                >
                  {stepTimer(entry)}
                </TimelineStepCard>
              ))}
            </ol>
          )}
        </section>

        {done.length > 0 ? (
          <details className="card p-3">
            <summary className="cursor-pointer text-base font-semibold text-brand">
              Done ({done.length})
            </summary>
            <ol className="mt-2 flex flex-col gap-2">
              {done.map((entry) => (
                <TimelineStepCard
                  key={`${entry.stepId}-${entry.start}`}
                  entry={entry}
                  recipeTitle={titleFor(entry.recipeId)}
                  done
                >
                  {stepTimer(entry)}
                </TimelineStepCard>
              ))}
            </ol>
          </details>
        ) : null}
      </main>

      <div className="sticky bottom-0 border-t border-line bg-surface px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto max-w-3xl">
          <Button onClick={() => setCookedOpen(true)}>Mark cooked</Button>
        </div>
      </div>

      {cookedOpen ? (
        <MarkCookedDialog
          open
          onClose={() => setCookedOpen(false)}
          planId={id}
          plan={data.plan}
          onCooked={(leftovers) =>
            void navigate('/plan', { state: { toast: cookedToast(leftovers) } })
          }
        />
      ) : null}
    </div>
  );
}
